import type { Cell, EntityId } from '../../sim';
import type { SendTarget } from '../sendTroops';

/**
 * What notifications are made of: the events the derivers raise from each
 * new state (derive/), and the feed entries they fold into (feed.ts), plus
 * the few constants both sides share.
 */

/**
 * Which hit an area's attack entry reports: a higher (or equal, the later)
 * one takes it over, a lower one only keeps it open. A hit own city outranks
 * everything; a killed scout outranks other hits, so its loss isn't lost (near
 * a hit city it gets its own entry instead).
 */
export const ATTACK_PRIORITY = { unit: 0, building: 1, unitLost: 2, city: 3 } as const;

export type NotificationKind =
  | 'discovery'
  | 'attack'
  | 'arrival'
  | 'plunder'
  | 'loss'
  | 'idle'
  | 'spy'
  | 'trained'
  | 'food'
  | 'cap'
  | 'upgradePaused'
  | 'upgraded'
  | 'era';

export interface GameEvent {
  kind: NotificationKind;
  text: string;
  /** Where to take the camera when the notification is clicked. */
  pos?: Cell;
  /** The own city it's about: clicking selects it (idle workers). */
  cityId?: EntityId;
  /**
   * The own unit or building an attack hit (see `withAttackers`), or the own
   * building a loss is about (its attack entry goes once it's lost).
   */
  entityId?: EntityId;
  /** What troops can be sent at: the rival found, or the attacker (Send troops). */
  target?: SendTarget;
  /** An own unit was killed (a scout): its text replaces a merged attack's. */
  unitLost?: boolean;
  /** An attack's rank for its area's entry (see ATTACK_PRIORITY); missing: a unit hit. */
  priority?: number;
  /** The rivals in sight fighting in the attack's area: "2 Artillery, 1 Tank". */
  attackers?: string;
  /** Urgent beyond its kind (an own spy exposed): the screen edge glows (see `urgentAlerts`). */
  urgent?: boolean;
  /** Own units or buildings lost: rolls up with other losses of its kind nearby (see `addEvents`). */
  tally?: LossTally;
  /** Workers idle in the city (idle entries): the count chip shows it. */
  idle?: number;
  /** Units done training: rolls up with the city's other ones (see `addEvents`). */
  trained?: TrainedTally;
  /**
   * Marks a condition: an entry that exists while something holds (food
   * running out) rather than a one-off. The feed keeps one entry per key,
   * updated in place by each event with it, and drops it on the first frame
   * with none: emit the event every frame while the condition holds (see
   * `addEvents`).
   */
  key?: string;
  /** A second line under the text (a condition's figures). */
  detail?: string;
  /** A condition's progress, 0..1: drawn as a thin bar under the text. */
  progress?: number;
  /** A short value shown beside the title, such as a countdown "0:40". */
  badge?: string;
  /** Label of the action button that selects `cityId` ("Open City 2"). */
  action?: string;
}

/** What a rolling trained entry counts: "5 Warriors trained in City 1". */
export interface TrainedTally {
  /** Unit name ("Warrior") → how many came out. */
  counts: Record<string, number>;
  /** The city they trained in: "City 1". */
  city: string;
}

/** What a rolling loss entry counts: "5 units lost near City 1: 3 Warriors, 2 Archers". */
export interface LossTally {
  what: 'units' | 'buildings';
  /** Name ("Warrior", "Farm", "Barracks site") → how many were lost. */
  counts: Record<string, number>;
  /** Groups whose every member is gone (units). */
  groups?: number[];
  /** The player's own names for some of those groups, by group number (client-local). */
  groupNames?: Record<number, string>;
  /** Where, from the nearest own city: "near City 1", "9 tiles east of City 2". */
  place: string;
}

export interface Notification extends GameEvent {
  id: number;
  /** Sim time the notification was raised. */
  time: number;
  /** Sim time of the latest event merged into it (attacks keep updating). */
  lastTime: number;
  /** Events merged into this one (arrivals at the same tile). */
  count: number;
  /**
   * Closed by the player. Attacks and losses stay in the list (hidden) until
   * the fighting stops, so further hits or losses in the same area don't
   * re-raise them. A condition stays hidden the same way until it clears.
   */
  dismissed?: boolean;
}

/** Hexes within which a new attack joins an existing attack notification. */
export const ATTACK_MERGE_RADIUS = 4;

export function hexDistance(a: Cell, b: Cell): number {
  return Math.max(Math.abs(a.q - b.q), Math.abs(a.r - b.r), Math.abs(a.q + a.r - b.q - b.r));
}
